import { useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { money, num, shortDate, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, Kpi, Loading, Panel, Pill, StatMini } from '../ui/kit'

interface Offer {
  id: string; code: string; status: string; source: string
  estimatedKg: number; readyFrom: string; readyTo: string | null
  notes: string | null
  indicativePricePerKg: number | null; indicativeValue: number | null
  scheduledFor: string | null; declinedReason: string | null
  collectedKg: number | null; createdAt: string
  cropName: string; cropId: string; claimedGradeCode: string | null
  farmerName: string; farmerCode: string; farmerId: string
  zoneName: string | null; collectionCentreName: string | null
  lotCode: string | null; fulfilmentPct: number | null
}

interface BoardRow {
  readyFrom: string; cropId: string; cropName: string
  offers: number; farmers: number
  offeredKg: number; unclaimedKg: number | null
  acceptedKg: number | null; scheduledKg: number | null
}

interface Route { id: string; code: string; zoneName: string; planDate: string }

const STATUS_TONE: Record<string, 'ok' | 'warn' | 'crit' | 'info' | 'neutral'> = {
  open: 'info', accepted: 'ok', scheduled: 'ok', collected: 'neutral',
  part_collected: 'warn', declined: 'warn', withdrawn: 'neutral', expired: 'neutral',
}

export function Offers() {
  // Anyone in the office can read the board — sales cannot commit to a buyer without
  // knowing what supply is coming. Replying to a farmer is ops's job, and the server
  // enforces that, so the buttons are simply not shown to people it would refuse.
  const canReply = session.has('admin', 'ops')
  const [status, setStatus] = useState('open')
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  const offers = useApi<Offer[]>(`/offers${status ? `?status=${status}` : ''}`, { deps: [status] })
  const board = useApi<BoardRow[]>('/offers/board')
  const routes = useApi<Route[]>('/routes/live')

  // Never let a failed request read as an empty board: "0 kg, every offer has an
  // answer" is a much worse lie than "we could not load this".
  const known = board.data != null && !board.error
  const rows = offers.data ?? []
  const totalOffered = (board.data ?? []).reduce((s, b) => s + Number(b.offeredKg ?? 0), 0)
  const unclaimed = (board.data ?? []).reduce((s, b) => s + Number(b.unclaimedKg ?? 0), 0)
  const farmers = new Set(rows.map((o) => o.farmerId)).size

  function reload() {
    offers.reload()
    board.reload()
  }

  async function act(o: Offer, action: 'accept' | 'decline' | 'schedule') {
    try {
      if (action === 'decline') {
        const reason = window.prompt(
          `Why are you passing on ${o.farmerName}'s ${num(o.estimatedKg, 0)} kg of ${o.cropName}?\n\n` +
          'The farmer reads this. Turn someone down with no reason and they stop offering.',
        )
        if (!reason?.trim()) return
        await api.post(`/offers/${o.id}/decline`, { reason: reason.trim() })
        setBanner({ tone: 'ok', text: `Declined, and ${o.farmerName} was told why.` })
      } else if (action === 'accept') {
        await api.post(`/offers/${o.id}/accept`)
        setBanner({ tone: 'ok', text: `Accepted. Schedule it onto a route so an agent actually turns up.` })
      } else {
        const available = routes.data ?? []
        if (available.length === 0) {
          setBanner({ tone: 'crit', text: 'No route is planned today to put this collection on.' })
          return
        }
        const pick = window.prompt(
          `Which route?\n\n${available.map((r, i) => `${i + 1}. ${r.code} — ${r.zoneName}`).join('\n')}\n\n` +
          'Enter a number:',
          '1',
        )
        const idx = Number(pick) - 1
        const route = available[idx]
        if (!route) return

        const when = window.prompt('Collect on which day?', o.readyFrom.slice(0, 10))
        if (!when) return

        const res = await api.post<{ seq: number }>(`/offers/${o.id}/schedule`, {
          routeId: route.id, collectOn: when,
        })
        setBanner({
          tone: 'ok',
          text: `Scheduled as stop ${res.seq} on ${route.code}. ${o.farmerName} can see the date now.`,
        })
      }
      reload()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'That did not work.' })
    }
  }

  return (
    <main className="page">
      <div className="page-head">
        <h1>Farmer offers</h1>
        <p>
          What farmers say they will have ready, and when. This is only what they expect — the
          weight you pay for is still whatever the scale says on the day you collect.
        </p>
      </div>

      <div className="stack">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>{null}</Banner>
        )}

        <div className="kpi-grid">
          <Kpi label="Offered" value={known ? num(totalOffered) : '—'} unit={known ? 'kg' : undefined}
            sub="across the days ahead" />
          <Kpi
            label="Nobody has replied"
            value={known ? num(unclaimed) : '—'}
            unit={known ? 'kg' : undefined}
            sub={!known ? 'not loaded' : unclaimed > 0 ? 'nobody has replied to these yet' : 'every offer has an answer'}
            tone={!known ? 'neutral' : unclaimed > 0 ? 'warn' : 'ok'}
          />
          <Kpi label="Farmers" value={offers.error ? '—' : farmers} sub="with something on the board" />
          <Kpi label="Offers shown" value={offers.error ? '—' : rows.length}
            sub={status ? titleCase(status) : 'All statuses'} />
        </div>

        <Panel title="What is coming, by crop and day" meta="what you could buy if you say yes">
          {board.error && <ErrorNote error={board.error} onRetry={board.reload} />}
          {board.loading && <Loading rows={3} />}
          {board.data?.length === 0 && (
            <Empty title="No offers yet">
              Farmers post what they will have from their own phones. Until they do, you are
              guessing at how much to send the vans out for.
            </Empty>
          )}
          {board.data && board.data.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Ready</th><th>Crop</th><th className="n">Offers</th><th className="n">Farmers</th>
                    <th className="n">Offered</th><th className="n">Unclaimed</th>
                    <th className="n">Accepted</th><th className="n">Scheduled</th>
                  </tr>
                </thead>
                <tbody>
                  {board.data.map((b) => (
                    <tr key={`${b.readyFrom}-${b.cropId}`} className={Number(b.unclaimedKg) > 0 ? 'attention' : ''}>
                      <td className="n">{shortDate(b.readyFrom)}</td>
                      <td className="strong">{b.cropName}</td>
                      <td className="n">{b.offers}</td>
                      <td className="n">{b.farmers}</td>
                      <td className="n">{num(b.offeredKg)} kg</td>
                      <td className="n">{num(b.unclaimedKg)} kg</td>
                      <td className="n">{num(b.acceptedKg)} kg</td>
                      <td className="n">{num(b.scheduledKg)} kg</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel
          title="Offers"
          meta={
            <select className="input" style={{ minHeight: 30, width: 'auto' }}
              value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="open">Open — needs a reply</option>
              <option value="accepted">Accepted</option>
              <option value="scheduled">Scheduled</option>
              <option value="collected">Collected</option>
              <option value="part_collected">Collected short</option>
              <option value="declined">Declined</option>
            </select>
          }
          footer={
            <>
              <StatMini label="Shown" value={rows.length} />
              <StatMini label="Offered" value={`${num(rows.reduce((s, o) => s + Number(o.estimatedKg), 0))} kg`} />
              <StatMini label="Worth today"
                value={money(rows.reduce((s, o) => s + Number(o.indicativeValue ?? 0), 0), { compact: true })} />
            </>
          }
        >
          {offers.error && <ErrorNote error={offers.error} onRetry={offers.reload} />}
          {offers.loading && <Loading rows={5} />}
          {offers.data?.length === 0 && (
            <Empty title={`No ${status ? titleCase(status).toLowerCase() : ''} offers`}>
              Change the filter, or wait for farmers to post.
            </Empty>
          )}
          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Ref</th><th>Farmer</th><th>Area</th><th>Crop</th>
                    <th className="n">Offered</th><th className="n">Ready</th>
                    <th className="n">Worth</th><th>Status</th>
                    <th className="n">Collected</th>{canReply && <th style={{ width: 180 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((o) => (
                    <tr key={o.id} className={o.status === 'open' ? 'attention' : ''}>
                      <td className="code strong">{o.code}</td>
                      <td className="truncate">{o.farmerName}</td>
                      <td className="truncate faint">{o.zoneName ?? '—'}</td>
                      <td>
                        {o.cropName}
                        {o.claimedGradeCode && <span className="faint"> {o.claimedGradeCode}</span>}
                      </td>
                      <td className="n">{num(o.estimatedKg, 0)} kg</td>
                      <td className="n">{shortDate(o.readyFrom)}</td>
                      <td className="n">{money(o.indicativeValue, { currency: false })}</td>
                      <td><Pill tone={STATUS_TONE[o.status] ?? 'neutral'}>{titleCase(o.status)}</Pill></td>
                      <td className="n">
                        {o.collectedKg == null
                          ? <span className="faint">—</span>
                          : <>{num(o.collectedKg, 1)} kg <span className="faint">{o.fulfilmentPct}%</span></>}
                      </td>
                      {canReply && (
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {o.status === 'open' && (
                          <>
                            <button className="btn quiet" style={btn} onClick={() => void act(o, 'accept')}>Accept</button>{' '}
                            <button className="btn quiet" style={{ ...btn, color: 'var(--crit)' }}
                              onClick={() => void act(o, 'decline')}>Decline</button>
                          </>
                        )}
                        {(o.status === 'open' || o.status === 'accepted') && (
                          <> <button className="btn" style={btn} onClick={() => void act(o, 'schedule')}>Schedule</button></>
                        )}
                      </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Banner tone="info" title="Why offered and collected rarely match">
          A farmer's guess and the scale are never quite the same, and that is fine. The gap is how
          you learn whose estimates to trust when planning a route — it is never a reason to pay
          anything other than what was weighed.
        </Banner>
      </div>
    </main>
  )
}

const btn = { minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' } as const
